import { CalendarIcon, CheckIcon, XMarkIcon } from "@heroicons/react/24/outline";
import {
    CALENDAR_EVENT_TIME_GRANULARITY_MINUTES,
    formatDateBlock,
    getCurrentTimezone,
} from "@sp-crm/core";
import { useMutation } from "@tanstack/react-query";
import { trpc } from "components/rpc/trpc";
import { DatePicker } from "components/ui/date-time-picker/date-picker";
import { TimePicker } from "components/ui/date-time-picker/time-picker";
import { Panel } from "components/ui/panel/panel";
import { PanelType } from "components/ui/panel/panel-type";
import { PrimaryButton } from "components/ui/primary-button";
import { SecondaryButton } from "components/ui/secondary-button";
import { TextArea } from "components/ui/textarea";
import { CalendarEventDto } from "components/visits/types";
import React, { useState } from "react";
import { handleEvent } from "util/user-events";

type OutcomeMode = "completed" | "canceled" | "reschedule";

const outcomeChoices: {
    key: OutcomeMode;
    label: string;
    icon: React.FC<{ className: string }>;
}[] = [
    { key: "completed", label: "Visit completed", icon: CheckIcon },
    { key: "canceled", label: "Visit canceled", icon: XMarkIcon },
    { key: "reschedule", label: "Reschedule visit", icon: CalendarIcon },
];

const tileClasses =
    "flex flex-col items-center gap-1.5 rounded-lg border px-2 py-3 text-xs font-medium transition-colors";

const selectedTileClasses =
    "border-brand-500 bg-brand-50 text-brand-800 ring-1 ring-brand-500";

const unselectedTileClasses = "border-gray-300 bg-white text-gray-700 hover:bg-gray-50";

export const visitHeading = (visit: CalendarEventDto): string => {
    const name =
        visit.parentContactName ??
        visit.businessName ??
        visit.communityName ??
        "Referral source";
    return visit.title?.trim() ? `${visit.title.trim()} · ${name}` : name;
};

export const visitSubheading = (visit: CalendarEventDto): string => {
    const dateBlock = formatDateBlock(visit.eventDate);
    const when = dateBlock ? `${dateBlock.month} ${dateBlock.day}` : null;
    const org =
        visit.businessName && visit.businessName !== visit.parentContactName
            ? visit.businessName
            : null;
    return [org, when].filter(Boolean).join(" · ");
};

interface AgendaVisitOutcomePanelProps {
    visit: CalendarEventDto;
    onClose: () => void;
    onChanged: () => void;
}

export const AgendaVisitOutcomePanel: React.FC<AgendaVisitOutcomePanelProps> = ({
    visit,
    onClose,
    onChanged,
}) => {
    const trpcClient = trpc.useTRPC();
    const [mode, setMode] = useState<OutcomeMode | null>(null);
    const [outcome, setOutcome] = useState("");
    const [eventDate, setEventDate] = useState(visit.eventDate);
    const [eventTime, setEventTime] = useState(visit.eventTime);

    const settle = () => {
        onChanged();
        onClose();
    };

    const updateMutation = useMutation({
        ...trpcClient.calendarEvents.update.mutationOptions(),
        onSuccess: settle,
    });

    const deleteMutation = useMutation({
        ...trpcClient.calendarEvents.delete.mutationOptions(),
        onSuccess: settle,
    });

    const isSaving = updateMutation.isPending || deleteMutation.isPending;

    const applyUpdate = (changes: {
        eventDate: string;
        eventTime: string | null;
        outcome: string | null;
    }) =>
        updateMutation.mutate({
            id: visit.id,
            userId: visit.userId,
            eventDuration: visit.eventDuration,
            eventTimeZone: getCurrentTimezone(),
            title: visit.title,
            privateNotes: visit.privateNotes,
            activityTypeId: visit.activityTypeId,
            ...changes,
        });

    const logCompleted = () => {
        handleEvent("marketing-plan-visit-mark-done");
        applyUpdate({
            eventDate: visit.eventDate,
            eventTime: visit.eventTime,
            outcome: outcome.trim(),
        });
    };

    const logCanceled = () => {
        handleEvent("marketing-plan-visit-canceled");
        deleteMutation.mutate({ id: visit.id });
    };

    const reschedule = () => {
        handleEvent("marketing-plan-visit-rescheduled");
        applyUpdate({ eventDate, eventTime, outcome: visit.outcome });
    };

    return (
        <Panel
            isOpen={true}
            onDismiss={onClose}
            type={PanelType.medium}
            headerText="Log outcome"
            hasCloseButton={true}>
            <div className="space-y-4">
                <div>
                    <div className="text-sm font-medium text-gray-700">
                        {visitHeading(visit)}
                    </div>
                    <div className="text-sm text-gray-500">{visitSubheading(visit)}</div>
                </div>
                <div>
                    <span className="mb-1.5 block text-sm font-medium text-gray-700">
                        What happened with this visit?
                    </span>
                    <div className="grid grid-cols-3 gap-2">
                        {outcomeChoices.map(choice => (
                            <button
                                key={choice.key}
                                type="button"
                                onClick={() => setMode(choice.key)}
                                className={`${tileClasses} ${
                                    mode === choice.key
                                        ? selectedTileClasses
                                        : unselectedTileClasses
                                }`}>
                                <choice.icon className="h-5 w-5" />
                                {choice.label}
                            </button>
                        ))}
                    </div>
                </div>
                {mode === "completed" ? (
                    <>
                        <TextArea
                            label="How did it go?"
                            rows={4}
                            value={outcome}
                            placeholder="e.g. Met with the discharge team, left brochures, promised two referrals"
                            onChange={e => setOutcome(e.target.value)}
                        />
                        <PanelActions
                            onClose={onClose}
                            onConfirm={logCompleted}
                            confirmLabel="Log completed visit"
                            disabled={!outcome.trim() || isSaving}
                        />
                    </>
                ) : null}
                {mode === "canceled" ? (
                    <>
                        <p className="text-sm text-gray-600">
                            This visit will be deleted. Your marketing plan will recommend
                            scheduling a new visit when one is due.
                        </p>
                        <PanelActions
                            onClose={onClose}
                            onConfirm={logCanceled}
                            confirmLabel="Delete visit"
                            disabled={isSaving}
                        />
                    </>
                ) : null}
                {mode === "reschedule" ? (
                    <>
                        <DatePicker
                            label="New date"
                            value={eventDate}
                            onChange={value => setEventDate(value ?? visit.eventDate)}
                            disabled={false}
                            required={true}
                            textSize="base"
                            onOpenChange={() => undefined}
                            minValue={null}
                            maxValue={null}
                            visibleYears={20}
                        />
                        <TimePicker
                            label="Time (optional)"
                            value={eventTime}
                            onChange={setEventTime}
                            disabled={false}
                            nativeStepMinutes={CALENDAR_EVENT_TIME_GRANULARITY_MINUTES}
                        />
                        <PanelActions
                            onClose={onClose}
                            onConfirm={reschedule}
                            confirmLabel="Reschedule visit"
                            disabled={!eventDate || isSaving}
                        />
                    </>
                ) : null}
            </div>
        </Panel>
    );
};

const PanelActions: React.FC<{
    onClose: () => void;
    onConfirm: () => void;
    confirmLabel: string;
    disabled: boolean;
}> = ({ onClose, onConfirm, confirmLabel, disabled }) => (
    <div className="flex justify-end gap-2">
        <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
        <PrimaryButton onClick={onConfirm} disabled={disabled}>
            {confirmLabel}
        </PrimaryButton>
    </div>
);
